/** Pure view helpers for the campaign console (`/campaigns`). */
import type { PillTone } from "../components/Pill";
import type {
  BundleItem,
  CampaignHead,
  ClosedAs,
  ConsoleThread,
  DueSource,
  MemberState,
  Participant,
  PersonState,
  ThreadStep,
  Turn,
} from "../api/console";

export const STATE_LABEL: Record<MemberState, string> = {
  planned: "Planned",
  approved: "Approved",
  contacted: "Contacted",
  in_dialogue: "In dialogue",
  closed: "Closed",
};

// Literal class names – Tailwind keeps only classes it finds in source.
export const STATE_BAR: Record<MemberState, string> = {
  planned: "bg-rule-strong",
  approved: "bg-accent-muted",
  contacted: "bg-accent",
  in_dialogue: "bg-warn",
  closed: "bg-ok",
};

export const CLOSED_LABEL: Record<ClosedAs | "other", string> = {
  qualified: "qualified",
  declined: "declined",
  no_reply: "no reply",
  bounced: "bounced",
  unsubscribed: "unsubscribed",
  referred: "referred",
  other: "closed",
};

export const CLOSE_OPTIONS: ClosedAs[] = ["qualified", "declined", "no_reply", "bounced", "unsubscribed", "referred"];

export const TURN: Record<Turn, { label: string; tone: PillTone }> = {
  us: { label: "our turn", tone: "signal" },
  them: { label: "their turn", tone: "muted" },
  christoph: { label: "your turn", tone: "alert" },
};

export const DUE_TONE: Record<DueSource, PillTone> = { legal: "ok", promised: "signal", default: "muted" };

export function memberStateTone(state: PersonState): PillTone {
  if (state === "in_dialogue") return "signal";
  if (state === "closed" || state === "parked") return "muted";
  return "ok";
}

export function memberStateLabel(state: PersonState): string {
  return state === "parked" ? "parked" : STATE_LABEL[state].toLowerCase();
}

/** "server sends" / "drafts – you send". */
export function deliveryLabel(delivery: CampaignHead["delivery"]): string {
  return delivery === "server" ? "server sends" : "drafts – you send";
}

/** "6 qualified · 2 no reply" for the closed box. */
export function closedLine(closed: Partial<Record<ClosedAs | "other", number>>): string {
  return (Object.entries(closed) as [ClosedAs | "other", number][])
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${CLOSED_LABEL[k] ?? k}`)
    .join(" · ");
}

const DATE = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit" });
const DATE_YEAR = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const DATE_TIME = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

/** Server timestamps are UTC; older serializers omit the zone. */
function parseInstant(iso: string): Date {
  const hasZone = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return new Date(dateOnly ? `${iso}T12:00:00Z` : hasZone ? iso : `${iso}Z`);
}

/** "22.10." in Berlin; the year when it is not this year's. */
export function berlinDate(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "–";
  const d = parseInstant(iso);
  if (isNaN(d.getTime())) return "–";
  return d.getUTCFullYear() === now.getUTCFullYear() ? DATE.format(d) : DATE_YEAR.format(d);
}

/** "22.10., 14:10" in Berlin. */
export function berlinDateTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = parseInstant(iso);
  return isNaN(d.getTime()) ? "–" : DATE_TIME.format(d);
}

/** Seconds since an instant (0 when unknown). */
export function ageSeconds(iso: string | null | undefined, nowMs: number): number {
  if (!iso) return 0;
  const t = parseInstant(iso).getTime();
  return isNaN(t) ? 0 : Math.max(0, (nowMs - t) / 1000);
}

/** The participants still in the conversation. */
export function currentParticipants(t: ConsoleThread): Participant[] {
  return t.participants.filter((p) => p.state !== "left");
}

export function participantLabel(p: Participant): string {
  return p.name || p.email || p.contact_id || "unknown";
}

/** The other side of a thread: the institution, else its first current person. */
export function counterpart(t: ConsoleThread): string {
  const first = currentParticipants(t)[0] ?? t.participants[0];
  return t.company_name || (first ? participantLabel(first) : null) || t.subject || "unknown";
}

/** Open threads first – Christoph's turn, then ours, then theirs – each by due date. */
export function sortThreads(threads: ConsoleThread[]): ConsoleThread[] {
  const turnRank: Record<Turn, number> = { christoph: 0, us: 1, them: 2 };
  return [...threads].sort((a, b) => {
    if (a.status !== b.status) return a.status === "open" ? -1 : 1;
    if (a.turn !== b.turn) return turnRank[a.turn] - turnRank[b.turn];
    const da = a.due?.on ?? "9999";
    const db = b.due?.on ?? "9999";
    return da.localeCompare(db);
  });
}

export function stepArrow(dir: ThreadStep["dir"]): string {
  return dir === "in" ? "←" : dir === "out" ? "→" : "·";
}

export function replyRate(replied: number, contacted: number): string {
  return contacted ? `${Math.round((replied / contacted) * 100)} %` : "–";
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string | undefined): boolean {
  return !!value && UUID_RE.test(value);
}

/** The slug of the campaign behind the old `/b2g` page. */
export const B2G_CAMPAIGN_SLUG = "market-access";

/** The console URL for an old `/b2g?...` link. */
export function b2gRedirectTarget(search: string): string {
  const old = new URLSearchParams(search);
  const next = new URLSearchParams();
  const view = old.get("view");
  const wave = old.get("wave");
  if (wave) {
    next.set("tab", "bundles");
    next.set("wave", wave);
  } else if (view === "markets") {
    next.set("tab", "markets");
  } else if (view === "suppressions") {
    next.set("tab", "dnc");
  } else {
    next.set("tab", "bundles");
  }
  const filter = old.get("filter");
  if (filter && next.get("tab") === "bundles") next.set("filter", filter);
  return `/campaigns/${B2G_CAMPAIGN_SLUG}?${next.toString()}`;
}

/** The console URL of an outreach wave. */
export function waveUrl(waveId: string): string {
  return `/campaigns/${B2G_CAMPAIGN_SLUG}?tab=bundles&wave=${waveId}`;
}

/** The console page of a campaign, on its bundles. */
export function campaignUrl(slug: string): string {
  return `/campaigns/${encodeURIComponent(slug)}?tab=bundles`;
}

/** The console URL of one bundle. */
export function bundleUrl(slug: string, bundleId: string): string {
  return `${campaignUrl(slug)}&bundle=${bundleId}`;
}

/** Email items that never go out: the seat withdrew them or the owner rejected them. */
export const RETIRED_ITEM_STATUSES = ["withdrawn", "rejected"];

/** A finished email item: drafted in the mailbox or sent by the server. */
function isDone(item: BundleItem): boolean {
  return item.status === "done" || !!item.sent_directly;
}

/** A bundle's email items in three groups – live, done, retired – each in server order. */
export function splitBundleItems(items: BundleItem[]): {
  live: BundleItem[];
  done: BundleItem[];
  retired: BundleItem[];
} {
  const retired = items.filter((i) => RETIRED_ITEM_STATUSES.includes(i.status));
  const done = items.filter((i) => !retired.includes(i) && isDone(i));
  return { live: items.filter((i) => !retired.includes(i) && !done.includes(i)), done, retired };
}

/** "2 drafted · 1 sent" – what became of the done items. */
export function doneSummary(items: BundleItem[]): string {
  const sent = items.filter((i) => !!i.sent_directly).length;
  const drafted = items.length - sent;
  return [drafted ? `${drafted} drafted` : "", sent ? `${sent} sent` : ""].filter(Boolean).join(" · ");
}

/** "Withdrawn" / "Rejected" / "Withdrawn or rejected" – the label of the retired group. */
export function retiredLabel(items: BundleItem[]): string {
  const withdrawn = items.some((i) => i.status === "withdrawn");
  const rejected = items.some((i) => i.status === "rejected");
  if (withdrawn && rejected) return "Withdrawn or rejected";
  return rejected ? "Rejected" : "Withdrawn";
}
